import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { X, Save, CheckCircle, AlertCircle, Eye, EyeOff, Copy, ClipboardCheck, Settings, History, CreditCard } from 'lucide-react';
import { useAuth } from '../auth/KeycloakContext';
import InlineEditField, { InlineSelectField } from '../components/InlineEditField';

interface MumbleServer {
  id: string;
  name: string;
}

interface SyncResult {
  performed: boolean;
  total_channels: number;
  synced_channels: number;
  failed_channels: number;
  total_users_synced: number;
  errors: string[];
}

interface AudioCastSyncResult {
  success: boolean;
  synced?: number;
  error?: string;
}

interface Tenant {
  id: string;
  subdomain: string;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  max_users: number;
  max_channels: number;
  mumble_server_id: string | null;
  admin_username: string | null;
  admin_password: string | null;
}

type TabType = 'general' | 'history' | 'subscription';

export default function TenantEdit() {
  const navigate = useNavigate();
  const { keycloak } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;

  const [activeTab, setActiveTab] = useState<TabType>('general');
  const [servers, setServers] = useState<MumbleServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [audiocastSyncResult, setAudiocastSyncResult] = useState<AudioCastSyncResult | null>(null);

  // Tenant data
  const [tenant, setTenant] = useState<Tenant | null>(null);

  // Admin user state (for existing tenants)
  const [showPassword, setShowPassword] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  // New tenant form
  const [newForm, setNewForm] = useState({
    subdomain: '',
    name: '',
    contact_email: '',
    contact_phone: '',
    max_users: 50,
    max_channels: 20,
    mumble_server_id: '',
    admin_username: '',
  });

  useEffect(() => {
    if (keycloak?.token) {
      loadData();
    }
  }, [id, keycloak?.token]);

  const loadData = async () => {
    setLoading(true);

    // Load servers
    const serversRes = await fetch('/api/mumble-servers', {
      headers: { 'Authorization': `Bearer ${keycloak?.token}` }
    });
    setServers(await serversRes.json());

    // Load tenant if editing
    if (id) {
      const tenantRes = await fetch(`/api/tenants/${id}`, {
        headers: { 'Authorization': `Bearer ${keycloak?.token}` }
      });
      if (tenantRes.ok) {
        const data = await tenantRes.json();
        setTenant({
          id: data.id,
          subdomain: data.subdomain,
          name: data.name,
          contact_email: data.contact_email || null,
          contact_phone: data.contact_phone || null,
          max_users: data.max_users,
          max_channels: data.max_channels,
          mumble_server_id: data.mumble_server_id || null,
          admin_username: data.admin_username || null,
          admin_password: data.admin_password || null,
        });
      }
    }

    setLoading(false);
  };

  const updateField = async (field: string, value: string | number | null) => {
    if (!id || !tenant) return;

    const payload: Record<string, unknown> = { [field]: value };

    const response = await fetch(`/api/tenants/${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${keycloak?.token}`
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error('Failed to update field');
    }

    const result = await response.json();

    // Update local state
    setTenant(prev => prev ? { ...prev, [field]: value } : null);

    // Check if sync was triggered
    if (result.sync?.performed || result.audiocast_sync) {
      if (result.sync?.performed) {
        setSyncResult(result.sync);
      }
      if (result.audiocast_sync) {
        setAudiocastSyncResult(result.audiocast_sync);
      }
    }
  };

  const handleCreateTenant = async () => {
    setSaving(true);
    setSyncResult(null);
    setAudiocastSyncResult(null);

    const payload: Record<string, unknown> = {
      ...newForm,
      contact_email: newForm.contact_email || null,
      contact_phone: newForm.contact_phone || null,
      mumble_server_id: newForm.mumble_server_id || null,
      admin_username: newForm.admin_username ? `${newForm.admin_username}@${newForm.subdomain}` : null,
    };

    try {
      const response = await fetch('/api/tenants', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${keycloak?.token}`
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.sync?.performed || result.audiocast_sync) {
        if (result.sync?.performed) {
          setSyncResult(result.sync);
        }
        if (result.audiocast_sync) {
          setAudiocastSyncResult(result.audiocast_sync);
        }
        setSaving(false);
      } else {
        navigate('/tenants');
      }
    } catch (error) {
      console.error('Failed to create tenant:', error);
      setSaving(false);
    }
  };

  const dismissSyncResult = () => {
    setSyncResult(null);
    setAudiocastSyncResult(null);
    navigate('/tenants');
  };

  const resetAdminPassword = async () => {
    if (!id) return;
    setResettingPassword(true);
    try {
      const response = await fetch(`/api/tenants/${id}/reset-admin-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${keycloak?.token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setTenant(prev => prev ? { ...prev, admin_password: result.password } : null);
        setShowPassword(true);
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
    } finally {
      setResettingPassword(false);
      setShowResetPasswordModal(false);
    }
  };

  const copyAccountData = async () => {
    if (!tenant) return;
    const text = `SAYses Zugangsdaten
───────────────────
Login:      https://${tenant.subdomain}.sayseswork.com
Benutzer:   ${tenant.admin_username || '(nicht vorhanden)'}
Passwort:   ${tenant.admin_password || '(nicht gespeichert)'}`;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Laden...</div>
      </div>
    );
  }

  // Saving/Syncing Progress Modal
  if (saving) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600"></div>
            <h2 className="text-xl font-bold text-slate-800">
              Synchronisierung läuft...
            </h2>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            Die Einrichtung wird mit dem Mumble-Server synchronisiert.
          </p>
          <div className="w-full bg-slate-200 rounded-full h-2.5 mb-2 overflow-hidden">
            <div
              className="bg-blue-600 h-2.5 rounded-full"
              style={{
                width: '30%',
                animation: 'indeterminate 1.5s ease-in-out infinite'
              }}
            ></div>
          </div>
          <style>{`
            @keyframes indeterminate {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(400%); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Sync Result Modal
  if (syncResult || audiocastSyncResult) {
    const channelSuccess = !syncResult || syncResult.failed_channels === 0;
    const audiocastSuccess = !audiocastSyncResult || audiocastSyncResult.success;
    const allSuccess = channelSuccess && audiocastSuccess;
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
          <div className="flex items-center gap-3 mb-4">
            {allSuccess ? (
              <CheckCircle className="text-green-500" size={28} />
            ) : (
              <AlertCircle className="text-amber-500" size={28} />
            )}
            <h2 className="text-xl font-bold text-slate-800">Synchronisierung</h2>
          </div>

          {syncResult && (
            <>
              <h3 className="text-sm font-medium text-slate-700 mb-2">Mumble-Server Kanäle</h3>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Kanäle gesamt:</span>
                    <span className="ml-2 font-medium">{syncResult.total_channels}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Synchronisiert:</span>
                    <span className="ml-2 font-medium text-green-600">{syncResult.synced_channels}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Fehlgeschlagen:</span>
                    <span className={`ml-2 font-medium ${syncResult.failed_channels > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                      {syncResult.failed_channels}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-500">Benutzer sync.:</span>
                    <span className="ml-2 font-medium">{syncResult.total_users_synced}</span>
                  </div>
                </div>
              </div>
              {syncResult.errors.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-red-600 mb-2">Fehler:</h3>
                  <div className="bg-red-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <ul className="text-sm text-red-700 space-y-1">
                      {syncResult.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {audiocastSyncResult && (
            <>
              <h3 className="text-sm font-medium text-slate-700 mb-2">AudioCast Dateien</h3>
              <div className={`rounded-lg p-4 mb-4 ${audiocastSyncResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="text-sm">
                  {audiocastSyncResult.success ? (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-green-500" size={16} />
                      <span className="text-green-700">{audiocastSyncResult.synced || 0} Dateien synchronisiert</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="text-red-500" size={16} />
                      <span className="text-red-700">Fehler: {audiocastSyncResult.error || 'Unbekannter Fehler'}</span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end">
            <button
              onClick={dismissSyncResult}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  }

  // New Tenant Form
  if (isNew) {
    return (
      <div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Neue Einrichtung</h1>
            <p className="text-slate-500 mt-1">Erstellen Sie eine neue Einrichtung</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/tenants')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 rounded-lg transition-colors"
            >
              <X size={18} />
              Abbrechen
            </button>
            <button
              onClick={handleCreateTenant}
              disabled={saving || !newForm.name || !newForm.subdomain || !newForm.admin_username}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                newForm.name && newForm.subdomain && newForm.admin_username
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-300 text-white cursor-not-allowed'
              }`}
            >
              <Save size={18} />
              Erstellen
            </button>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-2xl">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
              <input
                type="text"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="z.B. Feuerwehr Mitte"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Subdomain *</label>
              <div className="flex">
                <input
                  type="text"
                  value={newForm.subdomain}
                  onChange={(e) => setNewForm({ ...newForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  className="flex-1 bg-white border border-slate-300 rounded-l-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="feuerwehr-mitte"
                />
                <span className="bg-slate-100 border border-l-0 border-slate-300 rounded-r-lg px-4 py-2.5 text-slate-500">
                  .sayseswork.com
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mumble Server</label>
              <select
                value={newForm.mumble_server_id}
                onChange={(e) => setNewForm({ ...newForm, mumble_server_id: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">-- Kein Server --</option>
                {servers.map((server) => (
                  <option key={server.id} value={server.id}>{server.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max. Benutzer</label>
                <input
                  type="number"
                  value={newForm.max_users}
                  onChange={(e) => setNewForm({ ...newForm, max_users: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  min={1}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max. Kanäle</label>
                <input
                  type="number"
                  value={newForm.max_channels}
                  onChange={(e) => setNewForm({ ...newForm, max_channels: parseInt(e.target.value) || 0 })}
                  className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  min={1}
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-sm font-medium text-slate-700 mb-4">Administrator</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Benutzername *</label>
                  <div className="flex">
                    <input
                      type="text"
                      value={newForm.admin_username}
                      onChange={(e) => setNewForm({ ...newForm, admin_username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                      className="flex-1 bg-white border border-slate-300 rounded-l-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="admin"
                    />
                    <span className="bg-slate-100 border border-l-0 border-slate-300 rounded-r-lg px-4 py-2.5 text-slate-500">
                      @{newForm.subdomain || 'subdomain'}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-2">E-Mail (optional)</label>
                  <input
                    type="email"
                    value={newForm.contact_email}
                    onChange={(e) => setNewForm({ ...newForm, contact_email: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="kontakt@beispiel.de"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Telefon (optional)</label>
                  <input
                    type="tel"
                    value={newForm.contact_phone}
                    onChange={(e) => setNewForm({ ...newForm, contact_phone: e.target.value })}
                    className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-slate-800 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Existing Tenant - with Tabs
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">
          <button
            onClick={() => navigate('/tenants')}
            className="text-slate-500 hover:text-slate-700 transition-colors"
          >
            Einrichtungen
          </button>
          <span className="text-slate-400 mx-2">&gt;</span>
          <span>{tenant?.name || 'Einrichtung'}</span>
        </h1>
        <p className="text-slate-500 mt-1">
          {tenant?.subdomain}.sayseswork.com
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${
              activeTab === 'general'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <Settings size={18} />
            Allgemein
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <History size={18} />
            Testverlauf
          </button>
          <button
            onClick={() => setActiveTab('subscription')}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium transition-colors ${
              activeTab === 'subscription'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
            }`}
          >
            <CreditCard size={18} />
            Abonnement
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && tenant && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-2xl">
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
              <InlineEditField
                value={tenant.name}
                onSave={(value) => updateField('name', String(value))}
                placeholder="Name eingeben"
              />
            </div>

            {/* Subdomain - read-only for existing */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Subdomain</label>
              <div className="flex items-center gap-2 px-2 py-1.5">
                <span className="text-slate-800">{tenant.subdomain}</span>
                <span className="text-slate-500">.sayseswork.com</span>
              </div>
              <p className="text-sm text-slate-400 mt-1">Die Subdomain kann nicht geändert werden.</p>
            </div>

            {/* Mumble Server */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mumble Server</label>
              <InlineSelectField
                value={tenant.mumble_server_id || ''}
                options={servers.map(s => ({ value: s.id, label: s.name }))}
                onSave={(value) => updateField('mumble_server_id', value || null)}
                placeholder="-- Kein Server --"
              />
            </div>

            {/* Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max. Benutzer</label>
                <InlineEditField
                  value={tenant.max_users}
                  onSave={(value) => updateField('max_users', Number(value))}
                  type="number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Max. Kanäle</label>
                <InlineEditField
                  value={tenant.max_channels}
                  onSave={(value) => updateField('max_channels', Number(value))}
                  type="number"
                />
              </div>
            </div>

            {/* Admin Section */}
            <div className="border-t border-slate-200 pt-6">
              <h3 className="text-sm font-medium text-slate-700 mb-4">Administrator</h3>
              <div className="space-y-4">
                {/* Username - read-only */}
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Benutzername</label>
                  <div className="px-2 py-1.5">
                    <span className="text-slate-800 font-mono">
                      {tenant.admin_username || <span className="text-slate-400 italic font-sans">nicht vorhanden</span>}
                    </span>
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Temporäres Passwort</label>
                  <div className="flex items-center gap-3 px-2 py-1.5">
                    {tenant.admin_password ? (
                      <>
                        <span className="font-mono text-slate-900">
                          {showPassword ? tenant.admin_password : '********'}
                        </span>
                        <button
                          onClick={() => setShowPassword(!showPassword)}
                          className="p-1 text-slate-400 hover:text-blue-600 transition-colors"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </>
                    ) : (
                      <span className="text-slate-400 italic">nicht gespeichert</span>
                    )}
                    <button
                      onClick={() => setShowResetPasswordModal(true)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Passwort neu generieren
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={copyAccountData}
                      disabled={!tenant.admin_password}
                      className={`text-sm flex items-center gap-1 ${
                        copiedToClipboard ? 'text-green-600' : 'text-blue-600 hover:underline'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {copiedToClipboard ? (
                        <><ClipboardCheck size={14} /> Kopiert!</>
                      ) : (
                        <><Copy size={14} /> Accountdaten kopieren</>
                      )}
                    </button>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm text-slate-600 mb-2">E-Mail</label>
                  <InlineEditField
                    value={tenant.contact_email || ''}
                    onSave={(value) => updateField('contact_email', String(value) || null)}
                    type="email"
                    placeholder="kontakt@beispiel.de"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm text-slate-600 mb-2">Telefon</label>
                  <InlineEditField
                    value={tenant.contact_phone || ''}
                    onSave={(value) => updateField('contact_phone', String(value) || null)}
                    type="tel"
                    placeholder="+49 123 456789"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="text-center text-slate-500 py-12">
            <History size={48} className="mx-auto mb-4 text-slate-300" />
            <p>Testverlauf wird hier angezeigt.</p>
            <p className="text-sm text-slate-400 mt-2">Diese Funktion ist noch in Entwicklung.</p>
          </div>
        </div>
      )}

      {activeTab === 'subscription' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="text-center text-slate-500 py-12">
            <CreditCard size={48} className="mx-auto mb-4 text-slate-300" />
            <p>Abonnement-Informationen werden hier angezeigt.</p>
            <p className="text-sm text-slate-400 mt-2">Diese Funktion ist noch in Entwicklung.</p>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-800">Passwort neu generieren</h2>
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-slate-600">
                Es wird ein neues 8-stelliges temporäres Passwort generiert. Der Benutzer muss es bei seinem ersten Login ändern.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button
                onClick={() => setShowResetPasswordModal(false)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={resetAdminPassword}
                disabled={resettingPassword}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg transition-colors"
              >
                {resettingPassword ? 'Wird generiert...' : 'Generieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
